import {
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    TextInput
} from "ncore-ui-kit";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";
import {
    SearchIcon,
    UserIcon
} from "../../anatomy/icons";

const NAME = "TextInput";

const USAGE_CODE = `const [email, setEmail] = useState("");

<TextInput
    hintText="We send your invoice to this address."
    placeholder="name@company.com"
    spreadBehaviour="stretch"
    onChangeText={setEmail}
    isClearEnabled={true}
    isRequired={true}
    title="Email"
    value={email}
/>`;

const VALIDATION_CODE = `const [postalCode, setPostalCode] = useState("");

<TextInput
    validation={(text) => /^[0-9]+$/.test(text)}
    onChangeText={setPostalCode}
    title="Postal code"
    value={postalCode}
    maxLength={5}
/>`;

const TYPES = [
    "neutral",
    "danger",
    "warning",
    "success",
    "info"
] as const;

const SIZES = [
    "small",
    "medium",
    "large"
] as const;

const DIGITS_PATTERN = /^[0-9]+$/;

const getIsDigitsOnly = (text: string) => DIGITS_PATTERN.test(text);

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("textInputPage.typesTitle"),
        id: "types"
    },
    {
        title: t("section.sizes"),
        id: "sizes"
    },
    {
        title: t("textInputPage.actionsTitle"),
        id: "actions"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("textInputPage.validationTitle"),
        id: "validation"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const TextInputPage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        email,
        setEmail
    ] = useState("");

    const [
        query,
        setQuery
    ] = useState("");

    const [
        searchedQuery,
        setSearchedQuery
    ] = useState("");

    const [
        postalCode,
        setPostalCode
    ] = useState("");

    const typeHints: Record<(typeof TYPES)[number], string> = {
        success: t("textInputPage.hintSuccess"),
        warning: t("textInputPage.hintWarning"),
        danger: t("textInputPage.hintDanger"),
        info: t("textInputPage.hintInfo"),
        neutral: t("sample.hint")
    };

    const handleSearch = () => {
        setSearchedQuery(query);
    };

    return <Article>
        <ComponentHeader
            lead={t("textInputPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("textInputPage.overviewText")}
            </Paragraph>
            <Paragraph>
                {t("textInputPage.overviewAlternatives")}
            </Paragraph>
            <Preview
                caption={t("textInputPage.overviewCaption")}
                isStacked={true}
            >
                <TextInput
                    hintText={t("textInputPage.overviewHint")}
                    placeholder={t("sample.placeholder")}
                    spreadBehaviour="stretch"
                    title={t("sample.email")}
                    onChangeText={setEmail}
                    isClearEnabled={true}
                    isRequired={true}
                    value={email}
                />
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("textInputPage.typesTitle")}
            id="types"
        >
            <Paragraph>
                {t("textInputPage.typesText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    TYPES.map((type) => <TextInput
                        initialValue="ada@example.com"
                        hintText={typeHints[type]}
                        spreadBehaviour="stretch"
                        title={type}
                        type={type}
                        key={type}
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("section.sizes")}
            id="sizes"
        >
            <Paragraph>
                {t("textInputPage.sizesText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    SIZES.map((size) => <TextInput
                        placeholder={t("sample.search")}
                        spreadBehaviour="stretch"
                        icon={SearchIcon}
                        title={size}
                        size={size}
                        key={size}
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("textInputPage.actionsTitle")}
            id="actions"
        >
            <Paragraph>
                {t("textInputPage.actionsText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <TextInput
                    title={t("textInputPage.fullName")}
                    initialValue={t("sample.name")}
                    spreadBehaviour="stretch"
                    isClearEnabled={true}
                    icon={UserIcon}
                />
                <TextInput
                    title={t("sample.password")}
                    spreadBehaviour="stretch"
                    initialValue="ncore-2026"
                    variant="hidden"
                />
                <TextInput
                    hintText={searchedQuery ? t("textInputPage.searchedFor", [
                        searchedQuery
                    ]) : t("textInputPage.searchHint")}
                    placeholder={t("textInputPage.searchPlaceholder")}
                    rightIconAccessibilityLabel={t("sample.search")}
                    rightIconOnPress={handleSearch}
                    title={t("sample.search")}
                    spreadBehaviour="stretch"
                    onChangeText={setQuery}
                    rightIcon={SearchIcon}
                    value={query}
                />
            </Preview>
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("textInputPage.statesText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <TextInput
                    title={t("textInputPage.disabled")}
                    initialValue={t("sample.name")}
                    spreadBehaviour="stretch"
                    isDisabled={true}
                />
                <TextInput
                    title={t("textInputPage.readOnly")}
                    initialValue={t("sample.name")}
                    spreadBehaviour="stretch"
                    isReadOnly={true}
                />
                <TextInput
                    title={t("textInputPage.loading")}
                    initialValue={t("sample.name")}
                    spreadBehaviour="stretch"
                    isLoading={true}
                />
                <TextInput
                    placeholder={t("sample.placeholder")}
                    title={t("sample.email")}
                    spreadBehaviour="stretch"
                    isRequired={true}
                />
                <TextInput
                    placeholder={t("sample.placeholder")}
                    title={t("textInputPage.nickname")}
                    spreadBehaviour="stretch"
                    isOptional={true}
                />
            </Preview>
        </Section>
        <Section
            title={t("textInputPage.validationTitle")}
            id="validation"
        >
            <Paragraph>
                {t("textInputPage.validationText")}
            </Paragraph>
            <Preview
                caption={t("textInputPage.validationCaption")}
                isStacked={true}
            >
                <TextInput
                    hintText={t("textInputPage.validationHint")}
                    title={t("textInputPage.postalCode")}
                    validation={getIsDigitsOnly}
                    onChangeText={setPostalCode}
                    spreadBehaviour="stretch"
                    value={postalCode}
                    maxLength={5}
                />
            </Preview>
            <CodeBlock
                code={VALIDATION_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("textInputPage.a11yLabel"),
                    t("textInputPage.a11yHint"),
                    t("textInputPage.a11yStates"),
                    t("textInputPage.a11yActions"),
                    t("textInputPage.a11yIcons"),
                    t("textInputPage.a11yKeyboard"),
                    t("textInputPage.a11yDeprecated")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("textInputPage.propValue"),
                        type: "string",
                        name: "value"
                    },
                    {
                        description: t("textInputPage.propInitialValue"),
                        name: "initialValue",
                        type: "string"
                    },
                    {
                        description: t("textInputPage.propOnChangeText"),
                        type: "(value: string) => void",
                        name: "onChangeText"
                    },
                    {
                        description: t("textInputPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("textInputPage.propHintText"),
                        name: "hintText",
                        type: "string"
                    },
                    {
                        description: t("textInputPage.propPlaceholder"),
                        name: "placeholder",
                        type: "string"
                    },
                    {
                        type: "\"neutral\" | \"danger\" | \"warning\" | \"success\" | \"info\"",
                        description: t("textInputPage.propType"),
                        defaultValue: "\"neutral\"",
                        name: "type"
                    },
                    {
                        description: t("textInputPage.propVariant"),
                        type: "\"text\" | \"hidden\"",
                        defaultValue: "\"text\"",
                        name: "variant"
                    },
                    {
                        type: "\"small\" | \"medium\" | \"large\"",
                        description: t("textInputPage.propSize"),
                        name: "size"
                    },
                    {
                        description: t("textInputPage.propSpreadBehaviour"),
                        type: "\"baseline\" | \"stretch\" | \"free\"",
                        defaultValue: "\"baseline\"",
                        name: "spreadBehaviour"
                    },
                    {
                        description: t("textInputPage.propIsRequired"),
                        defaultValue: "false",
                        name: "isRequired",
                        type: "boolean"
                    },
                    {
                        description: t("textInputPage.propIsOptional"),
                        name: "isOptional",
                        type: "boolean"
                    },
                    {
                        description: t("textInputPage.propSubTitle"),
                        name: "subTitle",
                        type: "string"
                    },
                    {
                        description: t("textInputPage.propIsShowSubTitle"),
                        name: "isShowSubTitle",
                        type: "boolean"
                    },
                    {
                        description: t("textInputPage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("textInputPage.propIsReadOnly"),
                        defaultValue: "false",
                        name: "isReadOnly",
                        type: "boolean"
                    },
                    {
                        description: t("textInputPage.propIsLoading"),
                        defaultValue: "false",
                        name: "isLoading",
                        type: "boolean"
                    },
                    {
                        description: t("textInputPage.propIsClearEnabled"),
                        name: "isClearEnabled",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("textInputPage.propIsShowHideTextButton"),
                        name: "isShowHideTextButton",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("textInputPage.propValidation"),
                        type: "(text: string) => boolean",
                        name: "validation"
                    },
                    {
                        description: t("textInputPage.propIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        description: t("textInputPage.propIconOnPress"),
                        name: "iconOnPress",
                        type: "() => void"
                    },
                    {
                        description: t("textInputPage.propIconAccessibilityLabel"),
                        name: "iconAccessibilityLabel",
                        type: "string"
                    },
                    {
                        description: t("textInputPage.propRightIcon"),
                        type: "NCoreUIKitIcon",
                        name: "rightIcon"
                    },
                    {
                        description: t("textInputPage.propRightIconOnPress"),
                        name: "rightIconOnPress",
                        type: "() => void"
                    },
                    {
                        description: t("textInputPage.propRightIconAccessibilityLabel"),
                        name: "rightIconAccessibilityLabel",
                        type: "string"
                    },
                    {
                        description: t("textInputPage.propHintTextIcon"),
                        type: "NCoreUIKitIcon",
                        name: "hintTextIcon"
                    },
                    {
                        description: t("textInputPage.propIsShowHintTextIcon"),
                        name: "isShowHintTextIcon",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("textInputPage.propId"),
                        type: "string",
                        name: "id"
                    },
                    {
                        description: t("textInputPage.propCustomColor"),
                        type: "keyof ProjectColorPalette | string",
                        name: "customColor"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("textInputPage.propCustomTheme"),
                        name: "customTheme"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("textInputPage.propsNote")}
            </Paragraph>
            <Paragraph
                isMuted={true}
            >
                {t("textInputPage.refNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default TextInputPage;
